import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ReactElement } from 'react'
import type { PostEmbed } from '@/types/posts'
import { isSafeUrl } from '@/lib/url'
import { parseStandardDocumentUri, parseStandardPublicationUri } from '@/lib/standardSite'
import type { StandardPublicationRef } from '@/lib/standardSite'
import { useAuth } from '@/hooks/useAuth'
import { useSubscriptionToggle } from '@/hooks/useSubscriptionToggle'
import { useMackinacTabPrefs } from '@/hooks/useMackinacTabPrefs'
import { Spinner } from '@/components/Spinner'
import { strings } from '@/strings'

interface StandardSiteArticleCardProps {
    embed: PostEmbed
}

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return iso
    }
}

function InlineSubscribeButton({
    isSubscribed,
    isLoading,
    isStatusLoading,
    onToggle,
}: {
    isSubscribed: boolean
    isLoading: boolean
    isStatusLoading: boolean
    onToggle: () => void
}) {
    const [isHovered, setIsHovered] = useState(false)

    if (isStatusLoading && !isSubscribed) {
        return (
            <button
                type="button"
                disabled
                aria-label="Loading subscription status"
                className="px-2.5 py-1 rounded-full text-xs font-semibold opacity-50 border border-current"
            >
                <Spinner size="sm" color="current" />
            </button>
        )
    }

    return (
        <button
            type="button"
            aria-pressed={isSubscribed}
            aria-label={
                isSubscribed ? 'Unsubscribe from this publication' : 'Subscribe to this publication'
            }
            onClick={e => {
                e.stopPropagation()
                onToggle()
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
            disabled={isLoading}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors border ${
                isSubscribed
                    ? isHovered
                        ? 'border-red-500 text-red-500'
                        : 'border-current opacity-80'
                    : 'bg-white/20 border-current font-semibold hover:bg-white/30'
            } ${isLoading ? 'opacity-50' : ''}`}
        >
            {isSubscribed ? (isHovered ? 'Unsubscribe' : 'Subscribed') : 'Subscribe'}
        </button>
    )
}

function FooterActions({ pubRef }: { pubRef: StandardPublicationRef }) {
    const { session } = useAuth()
    const showSubscribe = !!session && session.did !== pubRef.did

    const { isSubscribed, isLoading, isStatusLoading, handleToggle } = useSubscriptionToggle(
        showSubscribe ? pubRef.uri : undefined,
        showSubscribe ? pubRef.did : undefined
    )

    if (!showSubscribe) return null

    return (
        <InlineSubscribeButton
            isSubscribed={isSubscribed}
            isLoading={isLoading}
            isStatusLoading={isStatusLoading}
            onToggle={handleToggle}
        />
    )
}

export function StandardSiteArticleCard({
    embed,
}: StandardSiteArticleCardProps): ReactElement | null {
    const { openArticlesExternally } = useMackinacTabPrefs()
    if (embed.type !== 'external' || !embed.external) return null
    const docRef = parseStandardDocumentUri(embed.associatedRefs)
    if (!docRef) return null

    const { external } = embed
    const author = external.associatedProfiles?.[0]
    const source = external.source
    const pubRef = parseStandardPublicationUri(embed.associatedRefs)

    const theme = source?.theme
    const footerBgStyle =
        theme?.backgroundRGB != null
            ? {
                  backgroundColor: `rgb(${theme.backgroundRGB})`,
                  color: theme.foregroundRGB != null ? `rgb(${theme.foregroundRGB})` : undefined,
              }
            : undefined

    const openExternal = openArticlesExternally && isSafeUrl(external.uri)

    const cardBody = (
        <>
            {external.thumb && (
                <img
                    src={external.thumb}
                    alt={external.title ?? ''}
                    className="w-full h-40 object-cover"
                    loading="lazy"
                    onError={e => {
                        e.currentTarget.style.display = 'none'
                    }}
                />
            )}
            <div className="p-3 space-y-1.5">
                {external.title && (
                    <p className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark line-clamp-2">
                        {external.title}
                    </p>
                )}
                {external.description && (
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark line-clamp-2">
                        {external.description}
                    </p>
                )}
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty createdAt string is not a usable date, same as missing */}
                {(external.createdAt || external.readingTime !== undefined) && (
                    <div className="flex items-center gap-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                        {external.createdAt && <span>{formatDate(external.createdAt)}</span>}
                        {external.createdAt && external.readingTime !== undefined && (
                            <span aria-hidden="true">·</span>
                        )}
                        {external.readingTime !== undefined && (
                            <span>{strings.articles.minRead(external.readingTime)}</span>
                        )}
                    </div>
                )}
            </div>
        </>
    )

    return (
        <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden hover:bg-border-light/10 dark:hover:bg-border-dark/10 transition-colors">
            {openExternal ? (
                <a
                    href={external.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={e => e.stopPropagation()}
                >
                    {cardBody}
                </a>
            ) : (
                <Link
                    to={`/profile/${docRef.did}/article/${docRef.rkey}`}
                    className="block"
                    onClick={e => e.stopPropagation()}
                >
                    {cardBody}
                </Link>
            )}
            {source?.title && (
                <div
                    data-testid="article-card-footer"
                    className={
                        footerBgStyle
                            ? 'px-3 py-2 flex flex-col gap-2'
                            : 'px-3 py-2 flex flex-col gap-2 border-t border-border-light dark:border-border-dark text-text-secondary-light dark:text-text-secondary-dark'
                    }
                    style={footerBgStyle}
                >
                    <div className="flex items-center justify-between gap-3">
                        {pubRef ? (
                            <Link
                                to={`/publication/${pubRef.did}/${pubRef.rkey}`}
                                onClick={e => e.stopPropagation()}
                                aria-label={`View ${source.title} publication`}
                                className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity"
                            >
                                {source.icon && (
                                    <img
                                        src={source.icon}
                                        alt=""
                                        className="w-4 h-4 rounded-sm object-cover shrink-0"
                                        loading="lazy"
                                        onError={e => {
                                            e.currentTarget.style.display = 'none'
                                        }}
                                    />
                                )}
                                <div className="min-w-0">
                                    <span className="text-xs font-medium truncate block">
                                        {source.title}
                                    </span>
                                    {author && (
                                        <span className="text-xs opacity-75 truncate block">
                                            {'by @'}
                                            {author.handle}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        ) : (
                            <div className="flex items-center gap-1.5 min-w-0">
                                {source.icon && (
                                    <img
                                        src={source.icon}
                                        alt=""
                                        className="w-4 h-4 rounded-sm object-cover shrink-0"
                                        loading="lazy"
                                        onError={e => {
                                            e.currentTarget.style.display = 'none'
                                        }}
                                    />
                                )}
                                <div className="min-w-0">
                                    <span className="text-xs font-medium truncate block">
                                        {source.title}
                                    </span>
                                    {author && (
                                        <span className="text-xs opacity-75 truncate block">
                                            {'by @'}
                                            {author.handle}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {pubRef && <FooterActions pubRef={pubRef} />}
                    </div>
                </div>
            )}
        </div>
    )
}
